import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, ReplaySubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppWindow } from '../AppWindow';
import { InterceptorHttpParams } from '../core/error/http-error-interceptor.service';

export const PRINCIPAL_RESOURCE_URL = '/api/v1/principal';
export const PASSWORD_RESET_RESOURCE = '/api/v1/password-reset';

/**
 *
 */
export interface IIdentity {
  /**
   * The Okta login (username) for the end user.
   */
  userName?: string | null;
  isAuthenticated: boolean;
  /**
   * User's full name in displayable form including all name parts, possibly including titles and suffixes,
   * ordered according to the user's locale and preferences.
   */
  fullName?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  email?: string | null;
  /**
   * An identity provider account id (ex. GUAM)
   */
  idpAccountId?: string;
  userType?: string;

  // id, which is the okta id, is present, but it is not to be used in the application.
}

export interface IPartnerInfo {
  bpn?: string;
}

export interface IPermission {
  acceptedLicenseAgreement?: boolean;
  userAccessManagement?: boolean;
  myClients?: boolean;
  changePassword?: boolean;
}

/**
 * Represents the user identity and security claims
 *
 * @remarks
 * This interface is loosely based on analogous classes that exist in Spring Security and .NET core.   The purpose
 * of this class it to track user identity and security claims only.  It is not intended for user preferences,
 * profile information or other information that would be needed outside the security and identity domain.
 */
export interface IPrincipal {
  identity: IIdentity;
  permissions?: IPermission;
  partnerDetails?: IPartnerInfo;
  // claims will be added here when needed.
}

export interface IRecoveryTokenVerificationResult {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private readonly _principal$ = new ReplaySubject<IPrincipal>(1);
  private _fetchPrincipal$: Subscription | undefined;

  constructor(
    private _httpClient: HttpClient,
    private _appWindow: AppWindow,
  ) {}

  private _isAuthenticated = false;

  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  /**
   * Attempts to get the Principal object
   */
  public get principal$(): Observable<IPrincipal> {
    this._ensurePrincipalFetched();
    return this._principal$;
  }

  /***
   * Attempts to get the authentication state
   */
  public get isAuthenticated$(): Observable<boolean> {
    return this.principal$.pipe(
      map((principal) => !!principal && !!principal.identity && principal.identity.isAuthenticated),
    );
  }

  public hasPermission$(permission: keyof IPermission): Observable<boolean | undefined> {
    return this.principal$.pipe(map((principal) => principal.permissions && principal.permissions[permission]));
  }

  public logout() {
    this._appWindow.location.href = '/logout';
  }

  public async initiatePasswordReset(userName: string): Promise<void> {
    await firstValueFrom(this._httpClient.post<void>(PASSWORD_RESET_RESOURCE, { userName }));
  }

  public async verifyRecoveryToken(recoveryToken: string): Promise<IRecoveryTokenVerificationResult> {
    // We expect a 400 error when it is a bad token.  Do not display the global error handler message
    // when it is a 400 error.
    const params = new InterceptorHttpParams(
      { statusCodesToIgnore: [400] },
      {
        recoveryToken,
      },
    );
    return firstValueFrom(this._httpClient.get<IRecoveryTokenVerificationResult>(PASSWORD_RESET_RESOURCE, { params }));
  }

  public async resetPassword(recoveryToken: string, password: string): Promise<void> {
    const params = new InterceptorHttpParams({ statusCodesToIgnore: [400, 401] });

    await firstValueFrom(this._httpClient.put<void>(PASSWORD_RESET_RESOURCE, { recoveryToken, password }, { params }));
  }

  public fetchUpdatedPrincipal(): Observable<IPrincipal> {
    this._fetchPrincipal();
    return this._principal$;
  }

  private _ensurePrincipalFetched(): void {
    if (this._fetchPrincipal$) {
      return;
    }
    this._fetchPrincipal();
  }

  private _fetchPrincipal(): void {
    this._fetchPrincipal$ = this._httpClient
      .get<IPrincipal>('/api/v1/principal', {
        withCredentials: true,
      })
      .subscribe((principal) => {
        this._principal$?.next(principal);
        this._isAuthenticated = principal?.identity?.isAuthenticated;
      });
  }
}
